/**
 * FormatOverrideContext - Dev-only context for runtime format constant overrides
 *
 * Allows real-time adjustments to visual format constants without editing files.
 * Used by dev control panel for instant visual feedback during design iteration.
 */

import { createContext, useContext, useState, type ReactNode } from 'react';
import { lineAngleFormat, type FormatOptions } from '@alchemiesolns/svg-format';

/**
 * Partial format overrides - only specify values you want to override
 */
export interface FormatOverrides {
    fontSize?: number;
    bond?: {
        thickness?: number;
        length?: number;
        offset?: number;
        wedgeHeight?: number;
    };
    lonePair?: {
        radius?: number;
        offset?: number;
        ratios?: {
            gap?: number;
        };
    };
    atom?: {
        carbon?: {
            scale?: number;
            scaleBig?: number;
        };
    };
    arrow?: {
        thickness?: number;
    };
}

interface FormatOverrideContextValue {
    overrides: FormatOverrides;
    setOverrides: (overrides: FormatOverrides) => void;
    resetOverrides: () => void;
    resolvedFormat: FormatOptions;
}

const FormatOverrideContext = createContext<FormatOverrideContextValue | undefined>(undefined);

/**
 * Deep merge format overrides with base format
 */
function mergeFormatOverrides(base: FormatOptions, overrides: FormatOverrides): FormatOptions {
    return {
        ...base,
        ...(overrides.fontSize !== undefined && { fontSize: overrides.fontSize }),
        bond: {
            ...base.bond,
            ...(overrides.bond?.thickness !== undefined && { thickness: overrides.bond.thickness }),
            ...(overrides.bond?.length !== undefined && { length: overrides.bond.length }),
            ...(overrides.bond?.offset !== undefined && { offset: overrides.bond.offset }),
            ...(overrides.bond?.wedgeHeight !== undefined && { wedgeHeight: overrides.bond.wedgeHeight }),
        },
        lonePair: {
            ...base.lonePair,
            ...(overrides.lonePair?.radius !== undefined && { radius: overrides.lonePair.radius }),
            ...(overrides.lonePair?.offset !== undefined && { offset: overrides.lonePair.offset }),
            ratios: {
                ...base.lonePair.ratios,
                ...(overrides.lonePair?.ratios?.gap !== undefined && { gap: overrides.lonePair.ratios.gap }),
            },
        },
        atom: {
            ...base.atom,
            carbon: {
                ...base.atom.carbon,
                ...(overrides.atom?.carbon?.scale !== undefined && { scale: overrides.atom.carbon.scale }),
                ...(overrides.atom?.carbon?.scaleBig !== undefined && { scaleBig: overrides.atom.carbon.scaleBig }),
            },
        },
        arrow: {
            ...base.arrow,
            ...(overrides.arrow?.thickness !== undefined && { thickness: overrides.arrow.thickness }),
        },
    };
}

/**
 * Provider component for format overrides
 */
export function FormatOverrideProvider({ children }: { children: ReactNode }) {
    const [overrides, setOverrides] = useState<FormatOverrides>({});

    const resetOverrides = () => setOverrides({});

    const resolvedFormat = mergeFormatOverrides(lineAngleFormat, overrides);

    return (
        <FormatOverrideContext.Provider
            value={{ overrides, setOverrides, resetOverrides, resolvedFormat }}
        >
            {children}
        </FormatOverrideContext.Provider>
    );
}

/**
 * Hook to access format constants with runtime overrides applied
 *
 * Falls back to base lineAngleFormat if context not available (production builds)
 */
export function useFormatOverride(): FormatOptions {
    const context = useContext(FormatOverrideContext);

    // If context not available (production or not wrapped), use base format
    if (!context) {
        return lineAngleFormat;
    }

    return context.resolvedFormat;
}

/**
 * Hook to access format override controls (dev-only)
 */
export function useFormatOverrideControls() {
    const context = useContext(FormatOverrideContext);

    if (!context) {
        throw new Error('useFormatOverrideControls must be used within FormatOverrideProvider');
    }

    return {
        overrides: context.overrides,
        setOverrides: context.setOverrides,
        resetOverrides: context.resetOverrides,
    };
}
